import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";
import SearchSelect from "./SearchSelect";

const InmobiliariaConfiguracion = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [config, setConfig] = useState(null);
  const [cuentasOptions, setCuentasOptions] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);

  const [ctasLoading, setCtasLoading] = useState(false);
  const [taxLoading, setTaxLoading] = useState(false);

  // Estado de los UDF de cobros en SAP (U_CTA_TRANSIT / U_CTA_CHEQUES)
  const [udfs, setUdfs] = useState(null);
  const [udfsBusy, setUdfsBusy] = useState(false);
  const [udfsMsg, setUdfsMsg] = useState("");

  const userCompanyDb = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
    return user?.CompanyDB;
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const [cfgRes] = await Promise.all([
        apiClient.get("/inmobiliaria/configuracion", {
          params: { CompanyDB: userCompanyDb },
        }),
      ]);

      const cfg = cfgRes?.data || null;
      setConfig(cfg);

      // Cuentas contables para selects
      setCtasLoading(true);
      try {
        const ctasRes = await apiClient.get("/inmobiliaria/cuentas-contables", {
          params: { CompanyDB: userCompanyDb },
        });
        setCuentasOptions(Array.isArray(ctasRes?.data) ? ctasRes.data : []);
      } finally {
        setCtasLoading(false);
      }

      // Impuestos para TaxCode
      setTaxLoading(true);
      try {
        const taxRes = await apiClient.get("/inmobiliaria/impuestos", {
          params: { CompanyDB: userCompanyDb },
        });
        setTaxOptions(Array.isArray(taxRes?.data) ? taxRes.data : []);
      } finally {
        setTaxLoading(false);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error cargando configuración");
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await loadAll();
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUdfs = async () => {
    try {
      const res = await apiClient.get("/inmobiliaria/cobros/udfs-status", {
        params: { CompanyDB: userCompanyDb },
      });
      setUdfs(res?.data?.campos || null);
    } catch {
      setUdfs(null);
    }
  };

  useEffect(() => {
    checkUdfs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSetupUdfs = async () => {
    setUdfsBusy(true);
    setUdfsMsg("");
    try {
      const res = await apiClient.post(
        "/inmobiliaria/cobros/setup-udfs",
        {},
        { params: { CompanyDB: userCompanyDb } }
      );
      const r = res?.data?.resultados || [];
      setUdfsMsg(r.map((x) => `${x.campo}: ${x.estado}${x.error ? ` (${x.error})` : ""}`).join(" · "));
      await checkUdfs();
    } catch (e) {
      setUdfsMsg(e?.response?.data?.error || e?.message || "Error creando los campos");
    } finally {
      setUdfsBusy(false);
    }
  };

  const onChangeField = (field, value) => {
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
  };

  // Opciones para los dropdowns buscables (listas largas).
  const cuentaOpts = useMemo(
    () => (cuentasOptions || []).map((o) => ({ value: o.Code, label: `${o.Code} - ${o.Name}` })),
    [cuentasOptions]
  );
  const taxOpts = useMemo(
    () => (taxOptions || []).map((t) => ({ value: t.TaxCode, label: `${t.TaxCode} - ${t.Name}` })),
    [taxOptions]
  );

  const detailLines = Array.isArray(config?.REM_PAR_DCollection) ? config.REM_PAR_DCollection : [];

  const onChangeDetailLine = (idx, field, value) => {
    setConfig((prev) => {
      const cur = prev || {};
      const next = { ...cur };
      const lines = Array.isArray(cur.REM_PAR_DCollection) ? [...cur.REM_PAR_DCollection] : [];
      const existing = lines[idx] && typeof lines[idx] === "object" ? { ...lines[idx] } : {};
      existing[field] = value;
      lines[idx] = existing;
      next.REM_PAR_DCollection = lines;
      return next;
    });
  };

  const onAddDetailLine = () => {
    setConfig((prev) => {
      const cur = prev || {};
      const next = { ...cur };
      const lines = Array.isArray(cur.REM_PAR_DCollection) ? [...cur.REM_PAR_DCollection] : [];
      const maxLineId = lines.reduce((acc, l) => {
        const v = Number(l?.LineId);
        return Number.isFinite(v) ? Math.max(acc, v) : acc;
      }, 0);
      lines.push({ LineId: maxLineId + 1, U_ANHO: "", U_CUENTA: "" });
      next.REM_PAR_DCollection = lines;
      return next;
    });
  };

  const onRemoveDetailLine = (idx) => {
    setConfig((prev) => {
      const cur = prev || {};
      const next = { ...cur };
      const lines = Array.isArray(cur.REM_PAR_DCollection) ? [...cur.REM_PAR_DCollection] : [];
      lines.splice(idx, 1);
      next.REM_PAR_DCollection = lines;
      return next;
    });
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (!config?.DocEntry) {
        // REM_PAR es UDO tipo datos maestros: la clave es Code.
        if (!config?.Code) {
          setError("No se encontró Code para guardar.");
          return;
        }
      }
      if (!config?.Code) {
        setError("No se encontró Code para guardar.");
        return;
      }
      await apiClient.patch(`/inmobiliaria/configuracion/${encodeURIComponent(config.Code)}`, {
        U_CCP: config.U_CCP ?? null,
        U_CLP: config.U_CLP ?? null,
        U_CTA_FACT: config.U_CTA_FACT ?? null,
        U_CTA_ALP: config.U_CTA_ALP ?? null,
        U_TAXCODE: config.U_TAXCODE ?? null,
        U_ASIENTO: config.U_ASIENTO ?? null,
        U_PROYECTO: config.U_PROYECTO ?? null,
        U_DESC: config.U_DESC ?? null,
        U_INT_ANUAL: config.U_INT_ANUAL ?? 0,
        U_DIAS_TOL: config.U_DIAS_TOL ?? 0,
        U_CTA_INTERES: config.U_CTA_INTERES ?? null,
        U_CTA_TRANSIT: config.U_CTA_TRANSIT ?? null,
        U_CTA_CHEQUES: config.U_CTA_CHEQUES ?? null,
        REM_PAR_DCollection: Array.isArray(config.REM_PAR_DCollection) ? config.REM_PAR_DCollection : [],
      });
      setMessage("Configuración actualizada correctamente.");
      await loadAll();
    } catch (e2) {
      setError(e2?.response?.data?.error || e2?.message || "Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Cargando configuración...</div>;

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h3 className="card-title mb-2 text-center">Configuraciones básicas</h3>
        <hr className="mt-0 mb-4" />

        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        {!config ? (
          <div className="alert alert-warning">No hay registros de configuración.</div>
        ) : (
          <form onSubmit={onSave}>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Code</label>
                <input className="form-control" value={config.Code || ""} disabled />
              </div>
              <div className="col-md-3">
                <label className="form-label">DocEntry</label>
                <input className="form-control" value={config.DocEntry ?? ""} disabled />
              </div>
              <div className="col-md-6" />

              <div className="col-md-6">
                <label className="form-label">Proyecto</label>
                <input
                  className="form-control"
                  value={config.U_PROYECTO || ""}
                  onChange={(e) => onChangeField("U_PROYECTO", e.target.value)}
                  placeholder="Nombre del proyecto"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Texto base</label>
                <input
                  className="form-control"
                  value={config.U_DESC || ""}
                  onChange={(e) => onChangeField("U_DESC", e.target.value)}
                  placeholder="Texto base para el detalle de la factura"
                />
              </div>
            </div>

            <h4 className="mt-4 mb-2 text-center">Configuraciones contables</h4>
            <hr className="mt-0 mb-3" />

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Generar provisión</label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="switch-asiento"
                    checked={String(config.U_ASIENTO || "N").toUpperCase() === "Y"}
                    onChange={(e) => onChangeField("U_ASIENTO", e.target.checked ? "Y" : "N")}
                  />
                  <label className="form-check-label" htmlFor="switch-asiento">
                    {String(config.U_ASIENTO || "N").toUpperCase() === "Y" ? "Sí" : "No"}
                  </label>
                </div>
              </div>

              <div className="col-md-6" />

              <div className="col-md-6">
                <label className="form-label">Cuenta contable corto plazo</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CCP || ""}
                  onChange={(v) => onChangeField("U_CCP", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Cuenta contable largo plazo</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CLP || ""}
                  onChange={(v) => onChangeField("U_CLP", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Cuenta Anticipo CP</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CTA_FACT || ""}
                  onChange={(v) => onChangeField("U_CTA_FACT", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Cuenta Anticipo LP</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CTA_ALP || ""}
                  onChange={(v) => onChangeField("U_CTA_ALP", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Código de impuesto</label>
                <SearchSelect
                  options={taxOpts}
                  value={config.U_TAXCODE || ""}
                  onChange={(v) => onChangeField("U_TAXCODE", v)}
                  isDisabled={taxLoading}
                  isLoading={taxLoading}
                />
              </div>
            </div>

            <h4 className="mt-4 mb-2 text-center">Configuración de mora</h4>
            <hr className="mt-0 mb-3" />

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Interés anual (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={config.U_INT_ANUAL ?? ""}
                  min={0}
                  step={0.01}
                  onChange={(e) => onChangeField("U_INT_ANUAL", e.target.value)}
                  placeholder="Ej: 18.5"
                />
                <div className="form-text">Porcentaje de interés anual para cálculo de mora.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Días de tolerancia</label>
                <input
                  type="number"
                  className="form-control"
                  value={config.U_DIAS_TOL ?? ""}
                  min={0}
                  step={1}
                  onChange={(e) => onChangeField("U_DIAS_TOL", e.target.value)}
                  placeholder="Ej: 5"
                />
                <div className="form-text">Días posteriores al vencimiento antes de aplicar mora.</div>
              </div>

              <div className="col-md-6">
                <label className="form-label">Cuenta contable de intereses</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CTA_INTERES || ""}
                  onChange={(v) => onChangeField("U_CTA_INTERES", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
                <div className="form-text">Cuenta contable donde se registran los intereses por mora.</div>
              </div>

              <div className="col-12">
                <div className="border rounded p-2 bg-light">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <strong className="small">Campos de cobros en SAP:</strong>
                    {udfs ? (
                      <>
                        <span className={`badge ${udfs.U_CTA_TRANSIT ? "bg-success" : "bg-secondary"}`}>
                          U_CTA_TRANSIT {udfs.U_CTA_TRANSIT ? "✓" : "falta"}
                        </span>
                        <span className={`badge ${udfs.U_CTA_CHEQUES ? "bg-success" : "bg-secondary"}`}>
                          U_CTA_CHEQUES {udfs.U_CTA_CHEQUES ? "✓" : "falta"}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted small">sin verificar</span>
                    )}
                    {udfs && !(udfs.U_CTA_TRANSIT && udfs.U_CTA_CHEQUES) && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm ms-auto"
                        onClick={onSetupUdfs}
                        disabled={udfsBusy}
                      >
                        {udfsBusy ? "Creando..." : "Crear campos en SAP"}
                      </button>
                    )}
                  </div>
                  {udfsMsg && <div className="small mt-1 text-muted">{udfsMsg}</div>}
                  <div className="form-text mb-0">
                    Crea los UDF en REM_PAR de esta sociedad. Ejecutar sin otros usuarios operando (bloquea la tabla).
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label">Cuenta transitoria (cobros)</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CTA_TRANSIT || ""}
                  onChange={(v) => onChangeField("U_CTA_TRANSIT", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
                <div className="form-text">
                  Cuenta puente para aplicar cobros en otra moneda a la factura.
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label">Cuenta puente de cheques</label>
                <SearchSelect
                  options={cuentaOpts}
                  value={config.U_CTA_CHEQUES || ""}
                  onChange={(v) => onChangeField("U_CTA_CHEQUES", v)}
                  isDisabled={ctasLoading}
                  isLoading={ctasLoading}
                />
                <div className="form-text">Cartera de cheques recibidos (hasta su depósito).</div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
              <button className="btn btn-outline-secondary" type="button" onClick={loadAll} disabled={saving}>
                Refrescar
              </button>
            </div>

            <hr className="my-4" />

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Detalle</h6>
              <button
                className="btn btn-sm btn-outline-primary"
                type="button"
                onClick={onAddDetailLine}
                disabled={saving}
              >
                Agregar línea
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>LineId</th>
                    <th>Año</th>
                    <th>Cuenta</th>
                    <th style={{ width: 1 }} />
                  </tr>
                </thead>
                <tbody>
                  {detailLines.map((l, idx) => (
                    <tr key={l.LineId ?? idx}>
                      <td>{l.LineId}</td>
                      <td style={{ minWidth: 120 }}>
                        <input
                          className="form-control form-control-sm"
                          value={l.U_ANHO ?? ""}
                          onChange={(e) => onChangeDetailLine(idx, "U_ANHO", e.target.value)}
                        />
                      </td>
                      <td style={{ minWidth: 240 }}>
                        <SearchSelect
                          options={cuentaOpts}
                          value={l.U_CUENTA ?? l.U_Cuenta ?? l.Cuenta ?? l.CUENTA ?? ""}
                          onChange={(v) => onChangeDetailLine(idx, "U_CUENTA", v)}
                          isDisabled={ctasLoading}
                          isLoading={ctasLoading}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          type="button"
                          onClick={() => onRemoveDetailLine(idx)}
                          disabled={saving}
                        >
                          Quitar
                        </button>
                      </td>
                    </tr>
                  ))}

                  {detailLines.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted">Sin líneas</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InmobiliariaConfiguracion;
